/**
 * DATABASE API ARCHITECTURE:
 * 
 * PUBLIC API FUNCTIONS (_public suffix):
 * - Accept external authentication provider IDs (e.g., Clerk IDs)
 * - Translate to internal Supabase UUIDs via user_profiles table
 * - Use for all frontend/API calls
 * 
 * ✅ CORRECT: Use _public functions with external auth IDs
 *    - has_document_access_public(p_document_id, p_clerk_user_id, p_access_type)
 *    - get_user_documents_public(p_clerk_user_id, ...)
 *    - delete_document_public(p_document_id, p_clerk_user_id)
 * 
 * ❌ WRONG: Never use internal UUID functions from frontend
 *    - has_document_access(p_document_id, p_user_uuid, ...) // Internal use only
 * 
 * External auth IDs: "user_32U5sWDI0SIMxwQUt1eKiWEgDVT" (from Clerk)
 * Internal UUIDs: "123e4567-e89b-12d3-a456-426614174000" (from user_profiles.id)
 */

import { supabase } from './supabase';
import type {
  UserProfile,
  UserRole,
  Permission,
  UserProfileWithPermissions,
  GetAllUsersResult,
  UserRoleUpdateRequest,
  UserCreateRequest,
  ApiResponse,
  Document,
  DocumentType,
  DocumentAccessLevel,
  DocumentOwnershipType,
  UserDocument,
  DocumentUploadRequest,
  DocumentShareRequest,
  DocumentAccessRequest,
  DocumentSharing,
  DocumentAuditLog,
  DocumentDealAssociationRequest,
  Deal,
  DealAccess,
  DealFinancialData,
  DealDocument,
  DealSummary,
  UserDeal,
  CreateDealRequest,
  UpdateDealRequest,
  GrantDealAccessRequest,
  RevokeDealAccessRequest,
  CreateDealFinancialDataRequest,
  CreateDealDocumentRequest,
  DealAccessType,
  Proforma,
  CreateProformaRequest,
  UpdateProformaRequest
} from '../types/database';

export class DatabaseService {
  // User Profile Management
  static async getUserProfile(userId?: string): Promise<ApiResponse<UserProfileWithPermissions>> {
    try {
      if (!userId) {
        return { error: 'User ID is required' };
      }

      // Use direct table query instead of non-existent RPC function
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, user_id, role, first_name, last_name, is_active, metadata, created_at, updated_at, last_login')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return { error: error.message };
      }

      if (!data) {
        return { error: 'User profile not found' };
      }

      // Load permissions for the user's role using RPC function to bypass RLS
      const { data: permissions, error: permissionsError } = await supabase
        .rpc('get_role_permissions_by_role', {
          p_role: data.role
        });

      if (permissionsError) {
        console.error('Error fetching role permissions:', permissionsError);
        // Continue without permissions rather than failing completely
      }

      // Permissions are returned directly as an array of permission_type strings
      const permissionList = permissions || [];

      // Return profile with permissions
      return { 
        data: {
          ...data,
          permissions: permissionList
        } as UserProfileWithPermissions 
      };
    } catch (error) {
      console.error('Unexpected error fetching user profile:', error);
      return { error: 'Failed to fetch user profile' };
    }
  }

  static async getAllUsers(userId?: string): Promise<ApiResponse<GetAllUsersResult[]>> {
    try {
      const { data, error } = await supabase.rpc('get_all_users', {
        p_user_id: userId || undefined
      });

      if (error) {
        console.error('Error fetching all users:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching all users:', error);
      return { error: 'Failed to fetch all users' };
    }
  }

  static async updateUserRole(request: UserRoleUpdateRequest, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data: _, error } = await supabase.rpc('update_user_role', {
        p_user_id: request.user_id,
        p_new_role: request.new_role,
        p_updated_by: clerkUserId
      });

      if (error) {
        console.error('Error updating user role:', error);
        return { error: error.message };
      }

      return { data: true, message: `User role updated to ${request.new_role}` };
    } catch (error) {
      console.error('Unexpected error updating user role:', error);
      return { error: 'Failed to update user role' };
    }
  }

  static async createUserProfile(request: UserCreateRequest): Promise<ApiResponse<UserProfile>> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          email: request.email,
          first_name: request.first_name,
          last_name: request.last_name,
          role: request.role,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        return { error: error.message };
      }

      return { data, message: 'User profile created successfully' };
    } catch (error) {
      console.error('Unexpected error creating user profile:', error);
      return { error: 'Failed to create user profile' };
    }
  }

  static async updateUserProfile(
    userId: string, 
    updates: Partial<Pick<UserProfile, 'first_name' | 'last_name' | 'is_active'>>
  ): Promise<ApiResponse<UserProfile>> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user profile:', error);
        return { error: error.message };
      }

      return { data, message: 'User profile updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating user profile:', error);
      return { error: 'Failed to update user profile' };
    }
  }

  static async deactivateUser(userId: string, adminUserId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/users/${userId}/deactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': adminUserId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to deactivate user' };
      }

      const data = await response.json();
      return { data, message: data.message };
    } catch (error) {
      console.error('Unexpected error deactivating user:', error);
      return { error: 'Failed to deactivate user' };
    }
  }

  static async reactivateUser(userId: string, adminUserId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/users/${userId}/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': adminUserId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to reactivate user' };
      }

      const data = await response.json();
      return { data, message: data.message };
    } catch (error) {
      console.error('Unexpected error reactivating user:', error);
      return { error: 'Failed to reactivate user' };
    }
  }

  static async toggleSuperAdmin(userId: string, adminUserId: string): Promise<ApiResponse<{ success: boolean; message: string; is_super_admin: boolean }>> {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/users/${userId}/toggle-super-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': adminUserId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to toggle Super Admin status' };
      }

      const data = await response.json();
      return { data, message: data.message };
    } catch (error) {
      console.error('Unexpected error toggling Super Admin:', error);
      return { error: 'Failed to toggle Super Admin status' };
    }
  }

  static async deleteUser(userId: string, adminUserId: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': adminUserId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to delete user' };
      }

      const data = await response.json();
      return { data, message: data.message };
    } catch (error) {
      console.error('Unexpected error deleting user:', error);
      return { error: 'Failed to delete user' };
    }
  }

  static async impersonateUser(userId: string, adminUserId: string): Promise<ApiResponse<{ 
    success: boolean; 
    token: string;
    url: string;
    user_id: string; 
    email: string; 
    name: string;
    expires_at: string;
    message: string 
  }>> {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/users/${userId}/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': adminUserId
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to generate impersonation token' };
      }

      const data = await response.json();
      return { data, message: data.message };
    } catch (error) {
      console.error('Unexpected error generating impersonation token:', error);
      return { error: 'Failed to generate impersonation token' };
    }
  }

  // Permission Checking
  static async checkPermission(permission: Permission, userId?: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_permission_public', {
        clerk_user_id: userId || undefined, // Use provided Clerk user ID or current user
        required_permission: permission
      });

      if (error) {
        console.error('Error checking permission:', error);
        return false;
      }

      const result = data || false;
      return result;
    } catch (error) {
      console.error('Unexpected error checking permission:', error);
      return false;
    }
  }

  static async getUserRole(clerkUserId?: string): Promise<UserRole | null> {
    try {
      // Get user profile first to get the internal UUID, then get role
      const { data: profile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId || undefined
      });

      if (profileError || !profile || profile.length === 0) {
        console.error('Error getting user profile for role:', profileError);
        return null;
      }

      const { data, error } = await supabase.rpc('get_user_role', {
        user_uuid: profile[0].id // Use the internal UUID from the profile
      });

      if (error) {
        console.error('Error getting user role:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Unexpected error getting user role:', error);
      return null;
    }
  }

  // Role Permissions
  static async getRolePermissions(): Promise<ApiResponse<Record<UserRole, Permission[]>>> {
    try {
      const { data, error } = await supabase
        .from('role_permissions_summary')
        .select('*');

      if (error) {
        console.error('Error fetching role permissions:', error);
        return { error: error.message };
      }

      const permissionsMap: Record<UserRole, Permission[]> = {
        Admin: [],
        Lender: [],
        Seller: [],
        Viewer: [],
        Buyer: [],
        Advisor: [],
        Attorney: []
      };

      data?.forEach((row: any) => {
        if (row.role in permissionsMap) {
          permissionsMap[row.role as UserRole] = row.permissions;
        }
      });

      return { data: permissionsMap };
    } catch (error) {
      console.error('Unexpected error fetching role permissions:', error);
      return { error: 'Failed to fetch role permissions' };
    }
  }

  static async updateRolePermissions(
    role: UserRole,
    permissions: Permission[],
    clerkUserId?: string
  ): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/admin/roles/${role}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Clerk-User-ID': clerkUserId
        },
        body: JSON.stringify(permissions)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to update role permissions' };
      }

      const data = await response.json();
      return { data: true, message: data.message };
    } catch (error) {
      console.error('Unexpected error updating role permissions:', error);
      return { error: 'Failed to update role permissions' };
    }
  }

  static async getRolePermissionsForRole(role: UserRole): Promise<ApiResponse<Permission[]>> {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('permission')
        .eq('role', role)
        .eq('granted', true);

      if (error) {
        console.error('Error fetching role permissions for role:', error);
        return { error: error.message };
      }

      const permissions = data?.map(row => row.permission as Permission) || [];
      return { data: permissions };
    } catch (error) {
      console.error('Unexpected error fetching role permissions for role:', error);
      return { error: 'Failed to fetch role permissions for role' };
    }
  }

  // Audit Log
  static async getAuditLog(limit: number = 50): Promise<ApiResponse<any[]>> {
    try {
      const { data, error } = await supabase
        .from('audit_log')
        .select(`
          *,
          user_profiles!audit_log_user_id_fkey(email, first_name, last_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching audit log:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching audit log:', error);
      return { error: 'Failed to fetch audit log' };
    }
  }

  // User Sessions
  static async getUserSessions(userId?: string): Promise<ApiResponse<any[]>> {
    try {
      let query = supabase
        .from('user_sessions')
        .select('*')
        .order('created_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching user sessions:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching user sessions:', error);
      return { error: 'Failed to fetch user sessions' };
    }
  }

  // Utility Methods
  static async isAdmin(): Promise<boolean> {
    return this.checkPermission('admin_access');
  }

  static async canManageUsers(): Promise<boolean> {
    return this.checkPermission('manage_users');
  }

  static async canReadFinancialData(): Promise<boolean> {
    return this.checkPermission('read_financial_data');
  }

  static async canWriteFinancialData(): Promise<boolean> {
    return this.checkPermission('write_financial_data');
  }

  static async canReadDealScenarios(): Promise<boolean> {
    return this.checkPermission('read_deal_scenarios');
  }

  static async canWriteDealScenarios(): Promise<boolean> {
    return this.checkPermission('write_deal_scenarios');
  }

  static async canReadLOIDocuments(): Promise<boolean> {
    return this.checkPermission('read_loi_documents');
  }

  static async canWriteLOIDocuments(): Promise<boolean> {
    return this.checkPermission('write_loi_documents');
  }

  static async canExportData(): Promise<boolean> {
    return this.checkPermission('export_data');
  }

  static async canViewAnalytics(): Promise<boolean> {
    return this.checkPermission('view_analytics');
  }

  // Document Management
  static async canManageDocuments(): Promise<boolean> {
    return this.checkPermission('manage_documents');
  }

  static async canUploadDocuments(clerkUserId?: string): Promise<boolean> {
    return this.checkPermission('upload_documents', clerkUserId);
  }

  static async canDownloadDocuments(clerkUserId?: string): Promise<boolean> {
    const result = await this.checkPermission('download_documents', clerkUserId);
    return result;
  }

  static async canShareDocuments(clerkUserId?: string): Promise<boolean> {
    return this.checkPermission('share_documents', clerkUserId);
  }

  static async canDeleteDocuments(clerkUserId?: string): Promise<boolean> {
    return this.checkPermission('delete_documents', clerkUserId);
  }

  static async canEditDocuments(clerkUserId?: string): Promise<boolean> {
    return this.checkPermission('edit_documents', clerkUserId);
  }

  // Document CRUD Operations
  static async getUserDocuments(
    documentType?: DocumentType,
    limit: number = 50,
    offset: number = 0,
    clerkUserId?: string
  ): Promise<ApiResponse<UserDocument[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }


      // Get user profile first
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', clerkUserId)
        .eq('is_active', true)
        .single();

      if (profileError || !userProfile) {
        console.error('User profile not found:', profileError);
        return { error: 'User profile not found' };
      }


      // Query documents directly with RLS
      let query = supabase
        .from('documents')
        .select(`
          id,
          title,
          description,
          document_type,
          file_name,
          file_path,
          file_size,
          mime_type,
          status,
          access_level,
          uploaded_by,
          uploaded_at,
          updated_at,
          version,
          is_active
        `)
        .eq('is_active', true)
        .order('uploaded_at', { ascending: false })
        .range(offset, offset + limit - 1);

      // Add document type filter if specified
      if (documentType) {
        query = query.eq('document_type', documentType);
      }

      const { data: documents, error: documentsError } = await query;


      if (documentsError) {
        console.error('Error fetching documents:', documentsError);
        return { error: documentsError.message };
      }

      // Transform the data to match UserDocument interface
      const transformedData = (documents || []).map((doc: any) => ({
        id: doc.id,
        title: doc.title,
        description: doc.description,
        document_type: doc.document_type,
        file_name: doc.file_name,
        file_size: doc.file_size,
        mime_type: doc.mime_type,
        status: doc.status,
        access_level: doc.access_level,
        uploaded_by: doc.uploaded_by,
        uploaded_at: doc.uploaded_at,
        updated_at: doc.updated_at,
        version: doc.version,
        can_read: true,
        can_write: userProfile.role === 'Admin' || userProfile.role === 'Buyer',
        can_admin: userProfile.role === 'Admin' || userProfile.role === 'Buyer',
        can_edit: userProfile.role === 'Admin' || userProfile.role === 'Buyer',
        can_delete: userProfile.role === 'Admin' || userProfile.role === 'Buyer',
        can_download: true,
        can_share: userProfile.role === 'Admin' || userProfile.role === 'Buyer',
        is_duplicate: false,
        duplicate_number: 0,
        document_tags: [
          // Add document type tag
          ...(doc.document_type && doc.document_type !== 'unknown' ? [{
            type: 'document_type',
            label: doc.document_type === 'profit_loss_statement' ? 'P&L' : 
                   doc.document_type === 'balance_sheet' ? 'Balance Sheet' :
                   doc.document_type === 'tax_return' ? 'Tax Return' :
                   doc.document_type === 'other' ? 'Other' : 
                   doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            color: 'blue'
          }] : []),
          // Add file extension tag
          ...(doc.file_name && doc.file_name.includes('.') ? [{
            type: 'file_extension',
            label: doc.file_name.split('.').pop()?.toUpperCase(),
            color: 'gray'
          }] : [])
        ]
      }));


      return { data: transformedData };
    } catch (error) {
      console.error('Unexpected error fetching user documents:', error);
      return { error: 'Failed to fetch user documents' };
    }
  }

  static async getDealDocumentDetails(
    dealId: string,
    clerkUserId?: string
  ): Promise<ApiResponse<UserDocument[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      
      // First check if user has access to the deal
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });


      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has access to the deal by checking if they are the creator or have explicit access
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('creator')
        .eq('id', dealId)
        .single();

      if (dealError) {
        return { error: 'Deal not found' };
      }

      const isDealCreator = dealData.creator === internalUserId;

      // Check for explicit access if not the creator
      let hasAccess = isDealCreator;
      if (!hasAccess) {
        const { data: accessData, error: accessError } = await supabase
          .from('deal_access')
          .select('access_type')
          .eq('deal_id', dealId)
          .eq('user_id', internalUserId)
          .eq('is_active', true)
          .single();

        hasAccess = !accessError && accessData;
      }

      if (!hasAccess) {
        return { error: 'User does not have access to this deal' };
      }

      
      // Use the existing RPC function for deal documents
      const { data: documents, error: documentsError } = await supabase.rpc('get_deal_documents_with_access', {
        p_clerk_user_id: clerkUserId,
        p_deal_id: dealId,
        p_document_type: null,
        p_limit: 100,
        p_offset: 0
      });
      
      
      // Documents are already filtered by the RPC function for the specific deal
      const filteredDocuments = documents || [];
      

      if (documentsError) {
        console.error('Error fetching documents:', documentsError);
        return { error: documentsError.message };
      }

      // Map documents to UserDocument format
      const userDocuments = filteredDocuments.map((doc: any) => ({
        id: doc.id,
        title: doc.title,
        file_url: doc.file_path,
        file_size: doc.file_size,
        document_type: doc.document_type,
        status: 'processed', // Default status since it's not in the function
        created_at: doc.uploaded_at,
        updated_at: doc.updated_at,
        uploaded_at: doc.uploaded_at, // Add uploaded_at for date display
        uploaded_by: userProfile[0].email || (userProfile[0].first_name + ' ' + userProfile[0].last_name).trim() || 'Unknown User', // Use user-friendly name
        deal_id: doc.deal_ids, // Use deal_ids instead of deal_id
        // Create document tags with type and file extension instead of deal names
        associated_deals: [], // Remove deal tags since user is already on deal-specific page
        document_tags: [
          // Add document type tag only if document_type exists and is valid
          ...(doc.document_type && doc.document_type !== 'unknown' ? [{
            type: 'document_type',
            label: doc.document_type === 'profit_loss_statement' ? 'P&L' : 
                   doc.document_type === 'balance_sheet' ? 'Balance Sheet' :
                   doc.document_type === 'tax_return' ? 'Tax Return' :
                   doc.document_type === 'other' ? 'Other' : 
                   doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            color: 'blue'
          }] : []),
          // Add file extension tag only if file_name exists and has extension
          ...(doc.file_name && doc.file_name.includes('.') ? [{
            type: 'file_extension',
            label: doc.file_name.split('.').pop()?.toUpperCase(),
            color: 'gray'
          }] : [])
        ],
        // Set permissions based on access level
        can_read: true,
        can_write: isDealCreator || userProfile[0].role === 'Admin',
        can_admin: isDealCreator || userProfile[0].role === 'Admin',
        can_edit: isDealCreator || userProfile[0].role === 'Admin',
        can_delete: isDealCreator || userProfile[0].role === 'Admin',
        can_download: true,
        can_share: isDealCreator || userProfile[0].role === 'Admin'
      }));


      return { data: userDocuments };
    } catch (error) {
      console.error('Unexpected error fetching deal document details:', error);
      return { error: 'Failed to fetch deal document details' };
    }
  }

  static async uploadDocument(request: DocumentUploadRequest, clerkUserId?: string): Promise<ApiResponse<Document>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check upload permission with user ID
      if (!(await this.canUploadDocuments(clerkUserId))) {
        return { error: 'Insufficient permissions to upload documents' };
      }

      // Get user profile for internal UUID
      const userProfileData = await this.getUserProfile(clerkUserId);
      if (userProfileData.error || !userProfileData.data) {
        return { error: 'Failed to get user profile' };
      }

      // Generate file hash for duplicate detection
      const fileBuffer = await request.file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', fileBuffer);
      const fileHash = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Upload file to Supabase Storage
      const fileExt = request.file.name.split('.').pop();
      const fileName = `${clerkUserId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, request.file);

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        return { error: uploadError.message };
      }

      // Create document record
      const { data, error } = await supabase
        .from('documents')
        .insert({
          title: request.title,
          description: request.description,
          document_type: request.document_type,
          file_name: request.file.name,
          file_path: uploadData.path,
          file_size: request.file.size,
          mime_type: request.file.type,
          file_hash: fileHash,
          access_level: request.access_level,
          uploaded_by: userProfileData.data.id,
          owner_id: userProfileData.data.id,
          expires_at: request.expires_at,
          metadata: request.metadata
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating document record:', error);
        return { error: error.message };
      }

      // Log the upload
      await supabase
        .from('document_audit_log')
        .insert({
          document_id: data.id,
          user_id: clerkUserId,
          action: 'uploaded',
          details: {
            file_name: request.file.name,
            file_size: request.file.size,
            document_type: request.document_type
          }
        });

      return { data, message: 'Document uploaded successfully' };
    } catch (error) {
      console.error('Unexpected error uploading document:', error);
      return { error: 'Failed to upload document' };
    }
  }

  static async bulkUploadDocuments(requests: DocumentUploadRequest[], clerkUserId?: string): Promise<ApiResponse<{ successful: Document[]; failed: { request: DocumentUploadRequest; error: string }[] }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check upload permission
      if (!(await this.canUploadDocuments(clerkUserId))) {
        return { error: 'Insufficient permissions to upload documents' };
      }

      // Get user profile for internal UUID
      const userProfileData = await this.getUserProfile(clerkUserId);
      if (userProfileData.error || !userProfileData.data) {
        return { error: 'Failed to get user profile' };
      }

      const successful: Document[] = [];
      const failed: { request: DocumentUploadRequest; error: string }[] = [];

      // Process each upload request
      for (const request of requests) {
        try {
          // Generate file hash for duplicate detection
          const fileBuffer = await request.file.arrayBuffer();
          const hashBuffer = await crypto.subtle.digest('SHA-256', fileBuffer);
          const fileHash = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

          // Upload file to Supabase Storage
          const fileExt = request.file.name.split('.').pop();
          const fileName = `${clerkUserId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('documents')
            .upload(fileName, request.file);

          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            failed.push({ request, error: uploadError.message });
            continue;
          }

          // Create document record
          const { data, error } = await supabase
            .from('documents')
            .insert({
              title: request.title,
              description: request.description,
              document_type: request.document_type,
              file_name: request.file.name,
              file_path: uploadData.path,
              file_size: request.file.size,
              mime_type: request.file.type,
              file_hash: fileHash,
              access_level: request.access_level,
              ownership_type: request.ownership_type,
              uploaded_by: userProfileData.data.id,
              owner_id: userProfileData.data.id,
              expires_at: request.expires_at,
              metadata: request.metadata
            })
            .select()
            .single();

          if (error) {
            console.error('Error creating document record:', error);
            failed.push({ request, error: error.message });
            continue;
          }

          // Log the upload
          await supabase
            .from('document_audit_log')
            .insert({
              document_id: data.id,
              user_id: clerkUserId,
              action: 'uploaded',
              details: {
                file_name: request.file.name,
                file_size: request.file.size,
                document_type: request.document_type
              }
            });

          successful.push(data);
        } catch (error) {
          console.error('Error processing upload request:', error);
          failed.push({ 
            request, 
            error: error instanceof Error ? error.message : 'Unknown error' 
          });
        }
      }

      return { 
        data: { successful, failed }, 
        message: `Successfully uploaded ${successful.length} of ${requests.length} documents` 
      };
    } catch (error) {
      console.error('Unexpected error in bulk upload:', error);
      return { error: 'Failed to process bulk upload' };
    }
  }

  static async downloadDocument(documentId: string, clerkUserId?: string): Promise<ApiResponse<{ url: string; fileName: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check download permission
      const hasDownloadPermission = await this.canDownloadDocuments(clerkUserId);
      if (!hasDownloadPermission) {
        return { error: 'Insufficient permissions to download documents' };
      }

      // Check document access (including deal access)
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_document_access_simple', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_access_type: 'read'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this document' };
      }

      // Get document details using Clerk-compatible function
      const { data: documentData, error: docError } = await supabase.rpc('get_document_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId
      });

      if (docError || !documentData || documentData.length === 0) {
        return { error: 'Document not found or access denied' };
      }

      const document = documentData[0];

      // Generate signed URL for download
      const { data: signedUrl, error: urlError } = await supabase.storage
        .from('documents')
        .createSignedUrl(document.file_path, 3600); // 1 hour expiry

      if (urlError) {
        console.error('Error generating download URL:', urlError);
        return { error: urlError.message };
      }

      // Try to fetch the PDF as a blob to create an object URL
      try {
        const response = await fetch(signedUrl.signedUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        
        // Create object URL from blob
        const objectUrl = URL.createObjectURL(blob);

        // Skip audit logging for now to avoid RLS issues
        // TODO: Implement proper audit logging with Clerk-compatible RLS policies

        // Use original filename from metadata if available, otherwise fall back to file_name
        const originalFileName = document.metadata?.original_filename || document.file_name;
        
        return { 
          data: { 
            url: objectUrl, 
            fileName: originalFileName
          },
          message: 'Download URL generated successfully'
        };
      } catch (fetchError) {
        console.error('Error fetching PDF as blob:', fetchError);
        // Fallback to signed URL
        // Use original filename from metadata if available, otherwise fall back to file_name
        const originalFileName = document.metadata?.original_filename || document.file_name;
        
        return { 
          data: { 
            url: signedUrl.signedUrl, 
            fileName: originalFileName
          },
          message: 'Download URL generated successfully'
        };
      }
    } catch (error) {
      console.error('Unexpected error downloading document:', error);
      return { error: 'Failed to download document' };
    }
  }

  // Specialized function for document extraction that has more permissive access
  static async getDocumentForExtraction(documentId: string, clerkUserId?: string): Promise<ApiResponse<{ url: string; fileName: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }


      // For extraction, we only need to verify the user can see the document in their list
      // This is more permissive than the strict download permissions
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_document_access_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_access_type: 'read'
      });


      if (accessError || !hasAccess) {
        return { error: 'Access denied to this document' };
      }

      // Get document details using Clerk-compatible function
      const { data: documentData, error: docError } = await supabase.rpc('get_document_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId
      });


      if (docError || !documentData || documentData.length === 0) {
        return { error: 'Document not found or access denied' };
      }

      const document = documentData[0];

      // Generate signed URL for download
      const { data: signedUrl, error: urlError } = await supabase.storage
        .from('documents')
        .createSignedUrl(document.file_path, 3600); // 1 hour expiry

      if (urlError) {
        console.error('Error generating download URL:', urlError);
        return { error: urlError.message };
      }


      // Try to fetch the PDF as a blob to create an object URL
      try {
        const response = await fetch(signedUrl.signedUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        return { 
          data: { 
            url: objectUrl, 
            fileName: document.file_name 
          } 
        };
      } catch (fetchError) {
        console.warn('Failed to create object URL, falling back to signed URL:', fetchError);
        // Fallback to signed URL if blob creation fails
        return { 
          data: { 
            url: signedUrl.signedUrl, 
            fileName: document.file_name 
          } 
        };
      }
    } catch (error) {
      console.error('Unexpected error in getDocumentForExtraction:', error);
      return { error: 'Failed to get document for extraction' };
    }
  }

  static async deleteDocument(documentId: string, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Note: Permission checking is now handled by the delete_document_public function
      // which allows document owners to delete their own documents

      // Delete document using Clerk-compatible function
      const { data: deleteResult, error: deleteError } = await supabase.rpc('delete_document_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId
      });

      if (deleteError || !deleteResult || deleteResult.length === 0 || !deleteResult[0].success) {
        return { error: 'Document not found or access denied' };
      }

      const document = deleteResult[0];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([document.file_path]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Continue with database deletion even if storage deletion fails
      }

      // Log the deletion (the audit log is already handled by the delete_document_public function)
      // No need to insert here as the database function already logs the deletion

      return { data: true, message: 'Document deleted successfully' };
    } catch (error) {
      console.error('Unexpected error deleting document:', error);
      return { error: 'Failed to delete document' };
    }
  }

  static async updateDocument(documentId: string, updates: {
    title?: string;
    description?: string;
    document_type?: DocumentType;
    access_level?: DocumentAccessLevel;
    ownership_type?: DocumentOwnershipType;
  }, clerkUserId?: string): Promise<ApiResponse<Document>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check edit permission
      if (!(await this.canEditDocuments(clerkUserId))) {
        return { error: 'Insufficient permissions to edit documents' };
      }

      // Get user profile to get UUID from Clerk user ID
      const { data: currentUserProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });


      if (profileError || !currentUserProfile || !Array.isArray(currentUserProfile) || currentUserProfile.length === 0) {
        console.error('Error getting user profile:', profileError);
        return { error: 'User profile not found' };
      }

      // Extract the user profile from the array
      const extractedUserProfile = currentUserProfile[0];

      // Check document access using the UUID
      
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_document_access_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_access_type: 'admin'
      });


      if (accessError || !hasAccess) {
        return { error: 'Access denied to edit this document' };
      }

      // Update document using Clerk-compatible function
      const { data: updateResult, error } = await supabase.rpc('update_document_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_title: updates.title || null,
        p_description: updates.description || null,
        p_document_type: updates.document_type || null,
        p_access_level: updates.access_level || null,
        p_ownership_type: updates.ownership_type || null
      });

      if (error || !updateResult || updateResult.length === 0) {
        return { error: 'Document not found or access denied' };
      }

      const data = updateResult[0];

      // Get internal user ID for audit log
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', clerkUserId)
        .single();

      // Log the update
      if (userProfile) {
        await supabase
          .from('document_audit_log')
          .insert({
            document_id: documentId,
            user_id: userProfile.id,
            action: 'updated',
            details: { 
              file_name: data.title, // Use the updated title
              changes: updates
            }
          });
      }

      return { data, message: 'Document updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating document:', error);
      return { error: 'Failed to update document' };
    }
  }

  static async createDocumentShare(request: DocumentShareRequest, clerkUserId?: string): Promise<ApiResponse<DocumentSharing>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check share permission
      if (!(await this.canShareDocuments(clerkUserId))) {
        return { error: 'Insufficient permissions to share documents' };
      }

      const { data, error } = await supabase.rpc('create_document_share', {
        p_document_id: request.document_id,
        p_access_type: request.access_type,
        p_expires_hours: request.expires_hours,
        p_max_downloads: request.max_downloads || null
      });

      if (error) {
        console.error('Error creating document share:', error);
        return { error: error.message };
      }

      return { data: data[0], message: 'Document share created successfully' };
    } catch (error) {
      console.error('Unexpected error creating document share:', error);
      return { error: 'Failed to create document share' };
    }
  }

  static async getDocumentShares(documentId: string, clerkUserId?: string): Promise<ApiResponse<DocumentSharing[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('document_sharing')
        .select('*')
        .eq('document_id', documentId)
        .eq('shared_by', clerkUserId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching document shares:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching document shares:', error);
      return { error: 'Failed to fetch document shares' };
    }
  }

  static async revokeDocumentShare(shareId: string, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { error } = await supabase
        .from('document_sharing')
        .update({ is_active: false })
        .eq('id', shareId)
        .eq('shared_by', clerkUserId);

      if (error) {
        console.error('Error revoking document share:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Document share revoked successfully' };
    } catch (error) {
      console.error('Unexpected error revoking document share:', error);
      return { error: 'Failed to revoke document share' };
    }
  }

  static async getDocumentAuditLog(documentId: string, clerkUserId?: string, limit: number = 50): Promise<ApiResponse<DocumentAuditLog[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check if user has access to document
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_document_access_public', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_access_type: 'read'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this document' };
      }

      const { data, error } = await supabase
        .from('document_audit_log')
        .select(`
          *,
          user_profiles!document_audit_log_user_id_fkey(email, first_name, last_name)
        `)
        .eq('document_id', documentId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching document audit log:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching document audit log:', error);
      return { error: 'Failed to fetch document audit log' };
    }
  }

  // Document-Deal Association Methods
  static async getDocumentsWithDealAssociations(clerkUserId?: string, dealId?: string): Promise<ApiResponse<UserDocument[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('get_documents_with_deal_associations', {
        p_clerk_user_id: clerkUserId,
        p_deal_id: dealId || null
      });

      if (error) {
        console.error('Error fetching documents with deal associations:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching documents with deal associations:', error);
      return { error: 'Failed to fetch documents with deal associations' };
    }
  }

  static async associateDocumentWithDeal(request: DocumentDealAssociationRequest, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }


      const { error } = await supabase.rpc('associate_document_with_deal', {
        p_document_id: request.document_id,
        p_deal_id: request.deal_id,
        p_association_type: request.association_type,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error associating document with deal:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Document associated with deal successfully' };
    } catch (error) {
      console.error('Unexpected error associating document with deal:', error);
      return { error: 'Failed to associate document with deal' };
    }
  }

  static async removeDocumentDealAssociation(documentId: string, dealId: string, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { error } = await supabase.rpc('remove_document_deal_association', {
        p_document_id: documentId,
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error removing document-deal association:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Document-deal association removed successfully' };
    } catch (error) {
      console.error('Unexpected error removing document-deal association:', error);
      return { error: 'Failed to remove document-deal association' };
    }
  }

  static async updateDocumentOwnershipType(documentId: string, ownershipType: DocumentOwnershipType, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { error } = await supabase.rpc('update_document_ownership_type', {
        p_document_id: documentId,
        p_ownership_type: ownershipType,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error updating document ownership type:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Document ownership type updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating document ownership type:', error);
      return { error: 'Failed to update document ownership type' };
    }
  }

  static async updateDocumentAccess(request: DocumentAccessRequest, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check if user has admin access to document
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_document_access_public', {
        p_document_id: request.document_id,
        p_clerk_user_id: clerkUserId,
        p_access_type: 'admin'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to manage this document' };
      }

      // Remove existing access for user/role
      const { error: deleteError } = await supabase
        .from('document_access')
        .delete()
        .eq('document_id', request.document_id)
        .or(`user_id.eq.${request.user_id || 'null'},role.eq.${request.role || 'null'}`);

      if (deleteError) {
        console.error('Error removing existing access:', deleteError);
        return { error: deleteError.message };
      }

      // Add new access
      const { error: insertError } = await supabase
        .from('document_access')
        .insert({
          document_id: request.document_id,
          user_id: request.user_id,
          role: request.role,
          access_type: request.access_type,
          granted_by: clerkUserId,
          expires_at: request.expires_at
        });

      if (insertError) {
        console.error('Error adding document access:', insertError);
        return { error: insertError.message };
      }

      return { data: true, message: 'Document access updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating document access:', error);
      return { error: 'Failed to update document access' };
    }
  }

  // Deal Management
  static async createDeal(request: CreateDealRequest, clerkUserId?: string): Promise<ApiResponse<Deal>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Use the database create_deal_new function to ensure proper business_alias generation
      const { data: dealData, error: dealError } = await supabase.rpc('create_deal_new', {
        p_title: request.business_name || request.title,
        p_description: request.description,
        p_user_id: clerkUserId, // Pass Clerk user ID as text
        p_deal_value: request.deal_value,
        p_currency: request.currency || 'USD',
        p_metadata: request.metadata || {}
      });

      if (dealError) {
        console.error('Error creating deal:', dealError);
        return { error: dealError.message };
      }

      // The create_deal function automatically grants admin access to the creator

      // Return the created deal data directly since we just created it
      const deal = dealData;

      return { data: deal, message: 'Deal created successfully' };
    } catch (error) {
      console.error('Unexpected error creating deal:', error);
      return { error: 'Failed to create deal' };
    }
  }

  static async getUserDeals(accessType: DealAccessType | null = null, clerkUserId?: string): Promise<ApiResponse<UserDeal[]>> {
    try {
      // Get the internal user ID from the user_profiles table
      let internalUserId: string | undefined;
      if (clerkUserId) {
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('user_id', clerkUserId)
          .eq('is_active', true)
          .single();
        
        if (profileError) {
          console.error('Error fetching user profile:', profileError);
          return { error: 'User not found' };
        }
        
        internalUserId = userProfile?.id;
      }
      
      console.log('[DEBUG] Calling get_user_deals_with_nda with:', { clerkUserId, accessType });
      
      const { data, error } = await supabase.rpc('get_user_deals_with_nda', {
        p_clerk_user_id: clerkUserId, // Use Clerk user ID
        p_user_id: null, // Don't use internal user ID
        p_access_type: accessType // Can be null to get all deals regardless of access level
      });

      console.log('[DEBUG] RPC response:', { data, error });

      if (error) {
        console.error('Error fetching user deals:', error);
        return { error: error.message };
      }

      console.log('[DEBUG] Returning data:', data);
      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching user deals:', error);
      return { error: 'Failed to fetch user deals' };
    }
  }

  static async getDealSummary(dealId: string): Promise<ApiResponse<DealSummary>> {
    try {
      const { data, error } = await supabase
        .from('deal_summary')
        .select('*')
        .eq('id', dealId)
        .single();

      if (error) {
        console.error('Error fetching deal summary:', error);
        return { error: error.message };
      }

      return { data };
    } catch (error) {
      console.error('Unexpected error fetching deal summary:', error);
      return { error: 'Failed to fetch deal summary' };
    }
  }

  static async getDeal(dealId: string): Promise<ApiResponse<Deal>> {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .single();


      if (error) {
        console.error('Error fetching deal:', error);
        return { error: error.message };
      }

      return { data };
    } catch (error) {
      console.error('Error fetching deal:', error);
      return { error: 'Failed to fetch deal' };
    }
  }

  static async updateDeal(request: UpdateDealRequest, clerkUserId?: string): Promise<ApiResponse<Deal>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has admin access to deal by checking deal_access table directly
      const { data: accessData, error: accessError } = await supabase
        .from('deal_access')
        .select('access_type')
        .eq('deal_id', request.id)
        .eq('user_id', internalUserId)
        .eq('is_active', true)
        .single();

      // Also check if user is the deal creator
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('creator')
        .eq('id', request.id)
        .single();

      const isDealCreator = dealData?.creator === internalUserId;
      const hasAdminAccess = accessData?.access_type === 'admin';

      if (accessError && !isDealCreator) {
        return { error: 'Access denied to update this deal' };
      }

      if (!isDealCreator && !hasAdminAccess) {
        return { error: 'Access denied to update this deal' };
      }

      const updateData: any = {};
      if (request.title !== undefined) updateData.title = request.title;
      if (request.business_name !== undefined) updateData.business_name = request.business_name;
      if (request.business_alias !== undefined) updateData.business_alias = request.business_alias;
      if (request.description !== undefined) updateData.description = request.description;
      if (request.status !== undefined) updateData.status = request.status;
      if (request.deal_value !== undefined) updateData.deal_value = request.deal_value;
      if (request.currency !== undefined) updateData.currency = request.currency;
      if (request.industry !== undefined) {
        updateData.industry = request.industry;
        // Also update business_alias when industry is set
        if (request.industry && request.industry.trim() !== '') {
          // Call the database function to update business_alias with industry
          const { error: aliasError } = await supabase.rpc('update_business_alias_with_industry', {
            p_deal_id: request.id,
            p_industry: request.industry
          });
          
          if (aliasError) {
            console.error('Error updating business alias:', aliasError);
            // Don't fail the entire update, just log the error
          }
        }
      }
      if (request.business_description !== undefined) updateData.business_description = request.business_description;
      if (request.business_type !== undefined) updateData.business_type = request.business_type;
      if (request.employee_count !== undefined) updateData.employee_count = request.employee_count;
      if (request.years_in_business !== undefined) updateData.years_in_business = request.years_in_business;
      // Note: metadata column doesn't exist in deals table, so we skip it


      const { data, error } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', request.id)
        .select()
        .single();


      if (error) {
        console.error('Error updating deal:', error);
        return { error: error.message };
      }

      return { data, message: 'Deal updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating deal:', error);
      return { error: 'Failed to update deal' };
    }
  }

  static async deleteDeal(dealId: string, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // First check if user is the deal creator
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('creator')
        .eq('id', dealId)
        .single();

      if (dealError) {
        console.error('Error fetching deal:', dealError);
        return { error: 'Deal not found' };
      }

      const isDealCreator = dealData.creator === internalUserId;

      // If not the creator, check for admin access
      if (!isDealCreator) {
        const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
          p_deal_id: dealId,
          p_user_id: internalUserId,
          p_required_access_type: 'admin'
        });

        if (accessError || !hasAccess) {
          return { error: 'Access denied to delete this deal' };
        }
      }

      // Soft delete the deal by setting is_active to false
      const { error } = await supabase
        .from('deals')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', dealId)
        .select()
        .single();

      if (error) {
        console.error('Error deleting deal:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Deal deleted successfully' };
    } catch (error) {
      console.error('Unexpected error deleting deal:', error);
      return { error: 'Failed to delete deal' };
    }
  }

  static async grantDealAccess(request: GrantDealAccessRequest): Promise<ApiResponse<string>> {
    try {
      const { data, error } = await supabase.rpc('grant_deal_access', {
        p_deal_id: request.deal_id,
        p_user_id: request.user_id,
        p_role: request.role,
        p_access_type: request.access_type,
        p_expires_at: request.expires_at
      });

      if (error) {
        console.error('Error granting deal access:', error);
        return { error: error.message };
      }

      return { data, message: 'Deal access granted successfully' };
    } catch (error) {
      console.error('Unexpected error granting deal access:', error);
      return { error: 'Failed to grant deal access' };
    }
  }

  static async revokeDealAccess(request: RevokeDealAccessRequest): Promise<ApiResponse<boolean>> {
    try {
      const { data: _, error } = await supabase.rpc('revoke_deal_access', {
        p_deal_id: request.deal_id,
        p_user_id: request.user_id,
        p_role: request.role
      });

      if (error) {
        console.error('Error revoking deal access:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Deal access revoked successfully' };
    } catch (error) {
      console.error('Unexpected error revoking deal access:', error);
      return { error: 'Failed to revoke deal access' };
    }
  }

  static async getDealAccess(dealId: string, clerkUserId?: string): Promise<ApiResponse<DealAccess[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Check if user has access to deal using the has_deal_access function
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: null, // Will be looked up from clerk user ID
        p_required_access_type: 'read',
        p_clerk_user_id: clerkUserId
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this deal' };
      }

      // Use the new function that includes the deal owner
      const { data, error } = await supabase.rpc('get_deal_access_with_owner', {
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error fetching deal access:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching deal access:', error);
      return { error: 'Failed to fetch deal access' };
    }
  }

  static async hasDealAccess(dealId: string, accessType: DealAccessType = 'read', clerkUserId?: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: null, // Will be looked up from clerk user ID
        p_required_access_type: accessType,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error checking deal access:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Unexpected error checking deal access:', error);
      return false;
    }
  }

  static async createDealFinancialData(request: CreateDealFinancialDataRequest, clerkUserId?: string): Promise<ApiResponse<DealFinancialData>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has write access to deal
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: request.deal_id,
        p_user_id: internalUserId,
        p_required_access_type: 'write'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to add financial data to this deal' };
      }

      const { data, error } = await supabase
        .from('deal_financial_data')
        .insert({
          deal_id: request.deal_id,
          data_type: request.data_type,
          period_start: request.period_start,
          period_end: request.period_end,
          amount: request.amount,
          currency: request.currency || 'USD',
          metadata: request.metadata || {},
          created_by: internalUserId
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating deal financial data:', error);
        return { error: error.message };
      }

      return { data, message: 'Financial data added successfully' };
    } catch (error) {
      console.error('Unexpected error creating deal financial data:', error);
      return { error: 'Failed to add financial data' };
    }
  }

  static async getDealFinancialData(dealId: string, clerkUserId?: string): Promise<ApiResponse<DealFinancialData[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID (following Document Extraction pattern)
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });


      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has access to deal using the has_deal_access function
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: internalUserId,
        p_required_access_type: 'read',
        p_clerk_user_id: clerkUserId
      });


      if (accessError || !hasAccess) {
        return { error: 'Access denied to this deal' };
      }

      const { data, error } = await supabase
        .from('deal_scenarios')
        .select('*')
        .eq('deal_id', dealId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });


      if (error) {
        console.error('Error fetching deal financial data:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching deal financial data:', error);
      return { error: 'Failed to fetch financial data' };
    }
  }

  static async createDealDocument(request: CreateDealDocumentRequest, clerkUserId?: string): Promise<ApiResponse<DealDocument>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has write access to deal
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: request.deal_id,
        p_user_id: internalUserId,
        p_required_access_type: 'write'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to add documents to this deal' };
      }

      // Use simplified function to add document to deal
      const { data, error } = await supabase.rpc('add_document_to_deal_simple', {
        p_document_id: request.document_id,
        p_deal_id: request.deal_id,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error creating deal document:', error);
        return { error: error.message };
      }

      return { data, message: 'Document linked to deal successfully' };
    } catch (error) {
      console.error('Unexpected error creating deal document:', error);
      return { error: 'Failed to link document to deal' };
    }
  }

  static async getDealDocuments(dealId: string, clerkUserId?: string): Promise<ApiResponse<DealDocument[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has access to deal
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: internalUserId,
        p_required_access_type: 'read'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this deal' };
      }

      // Use existing RPC function for deal documents
      const { data, error } = await supabase.rpc('get_deal_documents_with_access', {
        p_clerk_user_id: clerkUserId,
        p_deal_id: dealId,
        p_document_type: null,
        p_limit: 100,
        p_offset: 0
      });

      if (error) {
        console.error('Error fetching deal documents:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching deal documents:', error);
      return { error: 'Failed to fetch deal documents' };
    }
  }

  // LOI Document Operations
  static async getDealLOIDocuments(dealId: string, clerkUserId?: string): Promise<ApiResponse<any[]>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: userProfile, error: profileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (profileError || !userProfile || userProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = userProfile[0].id;

      // Check if user has access to deal
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: internalUserId,
        p_required_access_type: 'read'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this deal' };
      }

      const { data, error } = await supabase.rpc('get_deal_loi_documents', {
        p_deal_id: dealId
      });

      if (error) {
        console.error('Error fetching LOI documents:', error);
        return { error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Unexpected error fetching LOI documents:', error);
      return { error: 'Failed to fetch LOI documents' };
    }
  }

  static async createLOIDocument(dealId: string, loiData: any, clerkUserId?: string): Promise<ApiResponse<any>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      // Get user profile to get the internal user ID
      const { data: loiUserProfile, error: loiProfileError } = await supabase.rpc('get_user_profile_public', {
        clerk_user_id: clerkUserId
      });

      if (loiProfileError || !loiUserProfile || loiUserProfile.length === 0) {
        return { error: 'User profile not found' };
      }

      const internalUserId = loiUserProfile[0].id;

      // Check if user has write access to deal
      const { data: hasAccess, error: accessError } = await supabase.rpc('has_deal_access', {
        p_deal_id: dealId,
        p_user_id: internalUserId,
        p_required_access_type: 'write'
      });

      if (accessError || !hasAccess) {
        return { error: 'Access denied to this deal' };
      }

      // Get user profile for created_by
      const loiUserProfileData = await this.getUserProfile(clerkUserId);
      if (loiUserProfileData.error || !loiUserProfileData.data) {
        return { error: 'Failed to get user profile' };
      }

      const { data, error } = await supabase
        .from('loi_documents')
        .insert({
          deal_id: dealId,
          buyer_name: loiData.buyer_name,
          seller_name: loiData.seller_name,
          buyer_email: loiData.buyer_email,
          seller_email: loiData.seller_email,
          loi_type: loiData.loi_type || 'standard',
          selected_deal_scenario_id: loiData.selected_deal_scenario_id,
          purchase_price: loiData.purchase_price,
          currency: loiData.currency || 'USD',
          due_diligence_days: loiData.due_diligence_days || 30,
          exclusivity_days: loiData.exclusivity_days || 30,
          closing_date: loiData.closing_date,
          terms_and_conditions: loiData.terms_and_conditions,
          special_conditions: loiData.special_conditions,
          created_by: loiUserProfileData.data.id
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating LOI document:', error);
        return { error: error.message };
      }

      return { data };
    } catch (error) {
      console.error('Unexpected error creating LOI document:', error);
      return { error: 'Failed to create LOI document' };
    }
  }

  static async updateLOIDocument(loiId: string, updates: any, clerkUserId?: string): Promise<ApiResponse<any>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('loi_documents')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', loiId)
        .select()
        .single();

      if (error) {
        console.error('Error updating LOI document:', error);
        return { error: error.message };
      }

      return { data };
    } catch (error) {
      console.error('Unexpected error updating LOI document:', error);
      return { error: 'Failed to update LOI document' };
    }
  }

  static async addDigitalSignature(loiId: string, signatureData: any, clerkUserId?: string): Promise<ApiResponse<any>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('digital_signatures')
        .insert({
          loi_document_id: loiId,
          signer_type: signatureData.signer_type,
          signer_name: signatureData.signer_name,
          signer_email: signatureData.signer_email,
          signature_data: signatureData.signature_data,
          signed_at: signatureData.signed_at || new Date().toISOString(),
          status: 'signed'
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding digital signature:', error);
        return { error: error.message };
      }

      return { data };
    } catch (error) {
      console.error('Unexpected error adding digital signature:', error);
      return { error: 'Failed to add digital signature' };
    }
  }

  // Buyer Financial Analysis Operations
  static async getBuyerFinancialProfile(buyerId: string): Promise<ApiResponse<any>> {
    try {
      // Note: Authentication/authorization is handled at the component level
      // This method focuses on data retrieval based on the provided buyerId

      // First, get the internal user ID from the user_profiles table
      const { data: userProfile, error: userError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', buyerId)
        .single();

      if (userError) {
        console.error('Error fetching user profile:', userError);
        return { error: 'User profile not found' };
      }

      if (!userProfile) {
        return { data: null };
      }

      // Use SECURITY DEFINER RPC to fetch profile in one round-trip without RLS friction
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_buyer_financial_profile', {
        p_user_id: buyerId
      });

      if (rpcError) {
        console.error('Error fetching buyer profile (rpc):', rpcError);
        return { error: rpcError.message };
      }

      return { data: rpcData || null };
    } catch (error) {
      console.error('Unexpected error fetching buyer profile:', error);
      return { error: 'Failed to fetch buyer profile' };
    }
  }

  static async saveBuyerFinancialProfile(buyerId: string, profileData: any): Promise<ApiResponse<any>> {
    try {
      // Note: Authentication/authorization is handled at the component level
      // This method focuses on data persistence based on the provided buyerId

      // First, get the internal user ID from the user_profiles table
      const { data: targetUserProfile, error: targetUserError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', buyerId)
        .single();

      if (targetUserError) {
        console.error('Error fetching target user profile:', targetUserError);
        return { error: 'Target user profile not found' };
      }

      if (!targetUserProfile) {
        return { error: 'Target user profile not found' };
      }

      // Note: Permission checks are handled at the component level through useUserRole hook
      // This allows admins and the buyer themselves to update the profile

      // Start transaction by getting or creating buyer profile
      let buyerProfileId: string;
      
      const { data: existingProfile } = await supabase
        .from('buyer_financial_profiles')
        .select('id')
        .eq('buyer_id', targetUserProfile.id)
        .eq('is_active', true)
        .maybeSingle();

      if (existingProfile) {
        buyerProfileId = existingProfile.id;
        
        // Update existing profile
        const { error: updateError } = await supabase
          .from('buyer_financial_profiles')
          .update({
            total_assets: profileData.totalAssets,
            total_liabilities: profileData.totalLiabilities,
            net_worth: profileData.netWorth,
            monthly_income: profileData.monthlyIncome,
            monthly_expenses: profileData.monthlyExpenses,
            monthly_net_income: profileData.monthlyNetIncome,
            debt_to_income_ratio: profileData.debtToIncomeRatio,
            credit_utilization_ratio: profileData.creditUtilizationRatio,
            liquidity_ratio: profileData.liquidityRatio,
            risk_score: profileData.riskScore,
            sba_eligibility_score: profileData.sbaEligibilityScore,
            last_updated: new Date().toISOString()
          })
          .eq('id', buyerProfileId);

        if (updateError) {
          console.error('Error updating buyer profile:', updateError);
          return { error: updateError.message };
        }
      } else {
        // Create new profile
        const { data: newProfile, error: createError } = await supabase
          .from('buyer_financial_profiles')
          .insert({
            buyer_id: targetUserProfile.id,
            total_assets: profileData.totalAssets || 0,
            total_liabilities: profileData.totalLiabilities || 0,
            net_worth: profileData.netWorth || 0,
            monthly_income: profileData.monthlyIncome || 0,
            monthly_expenses: profileData.monthlyExpenses || 0,
            monthly_net_income: profileData.monthlyNetIncome || 0,
            debt_to_income_ratio: profileData.debtToIncomeRatio || 0,
            credit_utilization_ratio: profileData.creditUtilizationRatio || 0,
            liquidity_ratio: profileData.liquidityRatio || 0,
            risk_score: profileData.riskScore || 0,
            sba_eligibility_score: profileData.sbaEligibilityScore || 0
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating buyer profile:', createError);
          return { error: createError.message };
        }

        buyerProfileId = newProfile.id;
      }

      // Save credit scores via RPC
      if (profileData.creditScores) {
        const { error: csRpcError } = await supabase.rpc('upsert_credit_scores', {
          p_user_id: buyerId,
          p_scores: profileData.creditScores
        });
        if (csRpcError) {
          console.error('Error saving credit scores (rpc):', csRpcError);
          return { error: csRpcError.message };
        }
      }

      // Save bank accounts via RPC
      if (profileData.bankAccounts) {
        const { error: baRpcError } = await supabase.rpc('upsert_bank_accounts', {
          p_user_id: buyerId,
          p_accounts: profileData.bankAccounts
        });
        if (baRpcError) {
          console.error('Error saving bank accounts (rpc):', baRpcError);
          return { error: baRpcError.message };
        }
      }

      // Save SBA Personal Financial Statement via SECURITY DEFINER RPC to avoid client-side RLS issues
      if (profileData.sbaPersonalFinancialStatement) {
        const sbaData = profileData.sbaPersonalFinancialStatement;
        const { error: sbaRpcError } = await supabase.rpc('upsert_sba_pfs', {
          p_user_id: buyerId,
          p_personal_info: sbaData.personalInfo || {},
          p_monthly_net_worth: sbaData.monthlyNetWorth || 0,
          p_debt_to_income_ratio: sbaData.debtToIncomeRatio || 0,
          p_liquidity_ratio: sbaData.liquidityRatio || 0
        });

        if (sbaRpcError) {
          console.error('Error saving SBA statement (rpc):', sbaRpcError);
          return { error: sbaRpcError.message };
        }

        // Save assets via RPC
        if (sbaData.assets) {
          const { error: assetsRpcError } = await supabase.rpc('upsert_assets', {
            p_user_id: buyerId,
            p_assets: sbaData.assets
          });
          if (assetsRpcError) {
            console.error('Error saving assets (rpc):', assetsRpcError);
            return { error: assetsRpcError.message };
          }
        }

        // Save liabilities via RPC
        if (sbaData.liabilities) {
          const { error: liabRpcError } = await supabase.rpc('upsert_liabilities', {
            p_user_id: buyerId,
            p_liabilities: sbaData.liabilities
          });
          if (liabRpcError) {
            console.error('Error saving liabilities (rpc):', liabRpcError);
            return { error: liabRpcError.message };
          }
        }

        // Save income sources via RPC
        if (sbaData.incomeSources) {
          const { error: incRpcError } = await supabase.rpc('upsert_income_sources', {
            p_user_id: buyerId,
            p_income: sbaData.incomeSources
          });
          if (incRpcError) {
            console.error('Error saving income sources (rpc):', incRpcError);
            return { error: incRpcError.message };
          }
        }
      }

      // Save tax returns via RPC
      if (profileData.taxReturns) {
        const { error: trRpcError } = await supabase.rpc('upsert_tax_returns', {
          p_user_id: buyerId,
          p_returns: profileData.taxReturns
        });
        if (trRpcError) {
          console.error('Error saving tax returns (rpc):', trRpcError);
          return { error: trRpcError.message };
        }
      }

      return { data: { success: true, profileId: buyerProfileId }, message: 'Buyer financial profile saved successfully' };
    } catch (error) {
      console.error('Unexpected error saving buyer profile:', error);
      return { error: 'Failed to save buyer profile' };
    }
  }

  // DEPRECATED: Document sharing is now handled at the deal level
  // Simplified Document Access Management
  static async getDocumentUsersWithAccess(documentId: string, clerkUserId?: string): Promise<ApiResponse<any[]>> {
    console.warn('getDocumentUsersWithAccess is deprecated - document sharing is now handled at the deal level');
    return { error: 'This function is deprecated. Document access is now managed at the deal level.' };
    // try {
    //   if (!clerkUserId) {
    //     return { error: 'User not authenticated' };
    //   }

    //   const { data, error } = await supabase.rpc('get_document_users_with_access', {
    //     p_document_id: documentId,
    //     p_clerk_user_id: clerkUserId
    //   });

    //   if (error) {
    //     console.error('Error fetching document users with access:', error);
    //     return { error: error.message };
    //   }

    //   return { data: data || [] };
    // } catch (error) {
    //   console.error('Unexpected error fetching document users with access:', error);
    //   return { error: 'Failed to fetch document users with access' };
    // }
  }

  static async grantDocumentAccessByEmail(
    documentId: string, 
    email: string, 
    accessType: string = 'read',
    role?: string,
    clerkUserId?: string
  ): Promise<ApiResponse<{ success: boolean; message: string; user_id?: string; email: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('grant_document_access_by_email', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_email: email,
        p_access_type: accessType,
        p_role: role || null
      });

      if (error) {
        console.error('Error granting document access by email:', error);
        return { error: error.message };
      }

      if (data && data.length > 0) {
        const result = data[0];
        return { 
          data: {
            success: result.success,
            message: result.message,
            user_id: result.user_id,
            email: result.email
          }
        };
      }

      return { error: 'No result returned from grant access function' };
    } catch (error) {
      console.error('Unexpected error granting document access by email:', error);
      return { error: 'Failed to grant document access by email' };
    }
  }

  static async revokeDocumentAccess(
    documentId: string, 
    targetUserId: string, 
    clerkUserId?: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('revoke_document_access', {
        p_document_id: documentId,
        p_clerk_user_id: clerkUserId,
        p_target_user_id: targetUserId
      });

      if (error) {
        console.error('Error revoking document access:', error);
        return { error: error.message };
      }

      if (data && data.length > 0) {
        const result = data[0];
        return { 
          data: {
            success: result.success,
            message: result.message
          }
        };
      }

      return { error: 'No result returned from revoke access function' };
    } catch (error) {
      console.error('Unexpected error revoking document access:', error);
      return { error: 'Failed to revoke document access' };
    }
  }

  // DEPRECATED: Document sharing is now handled at the deal level
  static async getPreviouslySharedUsers(clerkUserId?: string): Promise<ApiResponse<any[]>> {
    console.warn('getPreviouslySharedUsers is deprecated - document sharing is now handled at the deal level');
    return { error: 'This function is deprecated. Document access is now managed at the deal level.' };
    // try {
    //   if (!clerkUserId) {
    //     return { error: 'User not authenticated' };
    //   }

    //   const { data, error } = await supabase.rpc('get_previously_shared_users', {
    //     p_clerk_user_id: clerkUserId
    //   });

    //   if (error) {
    //     console.error('Error fetching previously shared users:', error);
    //     return { error: error.message };
    //   }

    //   return { data: data || [] };
    // } catch (error) {
    //   console.error('Unexpected error fetching previously shared users:', error);
    //   return { error: 'Failed to fetch previously shared users' };
    // }
  }

  static async updateDocumentAccessLevel(
    documentId: string, 
    accessLevel: 'private' | 'shared', 
    clerkUserId?: string
  ): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { error } = await supabase
        .from('documents')
        .update({ access_level_simple: accessLevel })
        .eq('id', documentId);

      if (error) {
        console.error('Error updating document access level:', error);
        return { error: error.message };
      }

      return { data: true, message: 'Document access level updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating document access level:', error);
      return { error: 'Failed to update document access level' };
    }
  }

  // Proforma functions
  static async createProforma(
    request: CreateProformaRequest,
    clerkUserId?: string
  ): Promise<ApiResponse<Proforma>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('proforma')
        .insert({
          ...request,
          created_by: clerkUserId
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating proforma:', error);
        return { error: error.message };
      }

      return { data, message: 'Proforma created successfully' };
    } catch (error) {
      console.error('Unexpected error creating proforma:', error);
      return { error: error instanceof Error ? error.message : 'Failed to create proforma' };
    }
  }

  // Scenario Management (auth-provider agnostic public API; internal uses Clerk-compatible RPCs)
  static async listDealScenarios(dealId: string, clerkUserId?: string): Promise<ApiResponse<any[]>> {
    try {
      if (!clerkUserId) return { error: 'User not authenticated' };
      const { data, error } = await supabase.rpc('get_deal_scenarios_public', {
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId
      });
      if (error) return { error: error.message };
      return { data: data || [] };
    } catch (err) {
      return { error: 'Failed to list scenarios' };
    }
  }

  static async createDealScenario(dealId: string, scenarioData: any, clerkUserId?: string): Promise<ApiResponse<{ id: string }>> {
    try {
      if (!clerkUserId) return { error: 'User not authenticated' };
      
      // Prepare metadata with all the scenario data including name
      // Convert percentage rates to decimal rates (6.5% -> 0.065)
      const metadata = {
        name: scenarioData.name,
        headlinePrice: scenarioData.headlinePrice,
        businessName: scenarioData.businessName,
        bankAmount: scenarioData.bankAmount,
        bankRateAnnual: scenarioData.bankRateAnnual / 100, // Convert percentage to decimal
        bankTermYears: scenarioData.bankTermYears,
        sellerAmount: scenarioData.sellerAmount,
        sellerRateAnnual: scenarioData.sellerRateAnnual / 100, // Convert percentage to decimal
        sellerStructure: scenarioData.sellerStructure,
        sellerAmortizationYears: scenarioData.sellerAmortizationYears,
        sellerBalloonYears: scenarioData.sellerBalloonYears,
        sellerBalloonDueMonth: scenarioData.sellerBalloonDueMonth,
        sellerIoMonths: scenarioData.sellerIoMonths,
        annualSde: scenarioData.annualSde,
        monthlySde: scenarioData.monthlySde,
        financialMetricType: scenarioData.financialMetricType,
        annualThreshold: scenarioData.annualThreshold,
        quarterlyThreshold: scenarioData.quarterlyThreshold
      };

      const { data, error } = await supabase.rpc('create_deal_scenario_public', {
        p_clerk_user_id: clerkUserId,
        p_deal_id: dealId,
        p_metadata: metadata
      });
      if (error) return { error: error.message };
      const created = Array.isArray(data) ? data[0] : data;
      return { data: { id: created?.id }, message: 'Scenario created' };
    } catch (err) {
      return { error: 'Failed to create scenario' };
    }
  }

  static async cloneDealScenario(sourceScenarioId: string, name: string, clerkUserId?: string): Promise<ApiResponse<{ id: string }>> {
    try {
      if (!clerkUserId) return { error: 'User not authenticated' };
      const { data, error } = await supabase.rpc('clone_deal_scenario_public', {
        p_source_scenario_id: sourceScenarioId,
        p_name: name,
        p_clerk_user_id: clerkUserId
      });
      if (error) return { error: error.message };
      const created = Array.isArray(data) ? data[0] : data;
      return { data: { id: created?.id }, message: 'Scenario cloned' };
    } catch (err) {
      return { error: 'Failed to clone scenario' };
    }
  }

  static async updateDealScenario(scenarioId: string, updates: { name?: string; metadata?: Record<string, any> }, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) return { error: 'User not authenticated' };
      const { error } = await supabase.rpc('update_deal_scenario_public', {
        p_scenario_id: scenarioId,
        p_clerk_user_id: clerkUserId,
        p_name: updates.name ?? null,
        p_metadata: updates.metadata ?? null
      });
      if (error) return { error: error.message };
      return { data: true, message: 'Scenario updated' };
    } catch (err) {
      return { error: 'Failed to update scenario' };
    }
  }

  static async deleteDealScenario(scenarioId: string, clerkUserId?: string): Promise<ApiResponse<boolean>> {
    try {
      if (!clerkUserId) return { error: 'User not authenticated' };
      const { error } = await supabase.rpc('delete_deal_scenario_public', {
        p_scenario_id: scenarioId,
        p_clerk_user_id: clerkUserId
      });
      if (error) return { error: error.message };
      return { data: true, message: 'Scenario deleted' };
    } catch (err) {
      return { error: 'Failed to delete scenario' };
    }
  }

  static async updateProforma(
    request: UpdateProformaRequest,
    clerkUserId?: string
  ): Promise<ApiResponse<Proforma>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { id, ...updateData } = request;

      const { data, error } = await supabase
        .from('proforma')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating proforma:', error);
        return { error: error.message };
      }

      return { data, message: 'Proforma updated successfully' };
    } catch (error) {
      console.error('Unexpected error updating proforma:', error);
      return { error: error instanceof Error ? error.message : 'Failed to update proforma' };
    }
  }

  static async getProforma(
    proformaId: string,
    clerkUserId?: string
  ): Promise<ApiResponse<Proforma>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('proforma')
        .select('*')
        .eq('id', proformaId)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching proforma:', error);
        return { error: error.message };
      }

      return { data, message: 'Proforma fetched successfully' };
    } catch (error) {
      console.error('Unexpected error fetching proforma:', error);
      return { error: error instanceof Error ? error.message : 'Failed to fetch proforma' };
    }
  }

  static async getProformaByDealId(
    dealId: string,
    clerkUserId?: string
  ): Promise<ApiResponse<Proforma>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase
        .from('proforma')
        .select('*')
        .eq('deal_id', dealId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching proforma by deal ID:', error);
        return { error: error.message };
      }

      if (!data || data.length === 0) {
        return { data: undefined, message: 'No proforma found for this deal' };
      }

      return { data: data[0], message: 'Proforma fetched successfully' };
    } catch (error) {
      console.error('Unexpected error fetching proforma by deal ID:', error);
      return { error: error instanceof Error ? error.message : 'Failed to fetch proforma' };
    }
  }

  // Deal Deletion and Archive Functions
  
  /**
   * Permanently delete a deal and all related data
   */
  static async deleteDealCompletely(
    dealId: string,
    clerkUserId: string
  ): Promise<ApiResponse<{ success: boolean; message: string; deal_id: string; deleted_at: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('delete_deal_completely_public', {
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId,
        p_force_delete: false
      });

      if (error) {
        console.error('Error deleting deal:', error);
        return { error: error.message };
      }

      return { data, message: 'Deal deleted successfully' };
    } catch (error) {
      console.error('Unexpected error deleting deal:', error);
      return { error: error instanceof Error ? error.message : 'Failed to delete deal' };
    }
  }

  /**
   * Archive a deal to preserve data for later recovery
   */
  static async archiveDeal(
    dealId: string,
    clerkUserId: string,
    reason?: string
  ): Promise<ApiResponse<{ success: boolean; message: string; deal_id: string; archived_at: string; reason?: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('archive_deal_public', {
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId,
        p_reason: reason || null
      });

      if (error) {
        console.error('Error archiving deal:', error);
        return { error: error.message };
      }

      return { data, message: 'Deal archived successfully' };
    } catch (error) {
      console.error('Unexpected error archiving deal:', error);
      return { error: error instanceof Error ? error.message : 'Failed to archive deal' };
    }
  }

  /**
   * Restore an archived deal back to active status
   */
  static async restoreDeal(
    dealId: string,
    clerkUserId: string
  ): Promise<ApiResponse<{ success: boolean; message: string; deal_id: string; restored_at: string }>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('restore_deal_public', {
        p_deal_id: dealId,
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error restoring deal:', error);
        return { error: error.message };
      }

      return { data, message: 'Deal restored successfully' };
    } catch (error) {
      console.error('Unexpected error restoring deal:', error);
      return { error: error instanceof Error ? error.message : 'Failed to restore deal' };
    }
  }

  /**
   * Get all archived deals for the authenticated user
   */
  static async getArchivedDeals(
    clerkUserId: string
  ): Promise<ApiResponse<Array<{
    deal_id: string;
    business_name: string;
    business_alias?: string;
    description?: string;
    creator: string;
    status: string;
    deal_value?: number;
    currency: string;
    created_at: string;
    updated_at: string;
    archived_at: string;
    archived_by: string;
    archive_reason?: string;
    can_restore: boolean;
  }>>> {
    try {
      if (!clerkUserId) {
        return { error: 'User not authenticated' };
      }

      const { data, error } = await supabase.rpc('get_archived_deals_public', {
        p_clerk_user_id: clerkUserId
      });

      if (error) {
        console.error('Error fetching archived deals:', error);
        return { error: error.message };
      }

      return { data: data || [], message: 'Archived deals fetched successfully' };
    } catch (error) {
      console.error('Unexpected error fetching archived deals:', error);
      return { error: error instanceof Error ? error.message : 'Failed to fetch archived deals' };
    }
  }
}

// Export convenience functions
export const {
  getUserProfile,
  getAllUsers,
  updateUserRole,
  createUserProfile,
  updateUserProfile,
  deleteUser,
  checkPermission,
  getUserRole,
  getRolePermissions,
  updateRolePermissions,
  getRolePermissionsForRole,
  getAuditLog,
  getUserSessions,
  isAdmin,
  canManageUsers,
  canReadFinancialData,
  canWriteFinancialData,
  canReadDealScenarios,
  canWriteDealScenarios,
  canReadLOIDocuments,
  canWriteLOIDocuments,
  canExportData,
  canViewAnalytics,
  canManageDocuments,
  canUploadDocuments,
  canDownloadDocuments,
  canShareDocuments,
  canDeleteDocuments,
  canEditDocuments,
  getUserDocuments,
  uploadDocument,
  bulkUploadDocuments,
  downloadDocument,
  getDocumentForExtraction,
  updateDocument,
  deleteDocument,
  createDocumentShare,
  getDocumentShares,
  revokeDocumentShare,
  getDocumentAuditLog,
  getDocumentsWithDealAssociations,
  associateDocumentWithDeal,
  removeDocumentDealAssociation,
  updateDocumentOwnershipType,
  updateDocumentAccess,
  createDeal,
  getUserDeals,
  getDeal,
  getDealSummary,
  updateDeal,
  deleteDeal,
  grantDealAccess,
  revokeDealAccess,
  getDealAccess,
  hasDealAccess,
  createDealFinancialData,
  getDealFinancialData,
  createDealDocument,
  getDealDocuments,
  getDealDocumentDetails,
  getDealLOIDocuments,
  createLOIDocument,
  updateLOIDocument,
  addDigitalSignature,
  getBuyerFinancialProfile,
  saveBuyerFinancialProfile,
  getDocumentUsersWithAccess,
  grantDocumentAccessByEmail,
  revokeDocumentAccess,
  getPreviouslySharedUsers,
  updateDocumentAccessLevel,
  createProforma,
  updateProforma,
  getProforma,
  getProformaByDealId,
  deleteDealCompletely,
  archiveDeal,
  restoreDeal,
  getArchivedDeals
} = DatabaseService;
